/**
 * MatchDetail — full detail card for a single match.
 *
 * Composition (Task 21):
 *   - Header card    : participants, score, status, origin, badges, photo
 *   - AdminCreatedBadge if `created_by_admin_id` is non-null
 *   - Tester-bypass artifact warning if `has_tester_bypass_artifact`
 *   - MatchJourneyCard: every recorded step (status changes, pictures, notifications)
 *   - FlagPanel      : flag / unflag (gated `matches.flag`)
 *   - SummariesCard   : 4-cell grid + drill-downs (gated `matches.summaries.read`)
 *   - AuditCard       : last 20 admin actions
 *
 * Permission gate (call site): `matches.read` for the page route. This
 * component does not re-check; it relies on the route guard.
 *
 * Used by:
 *   - /matches (split-pane right pane on wide viewports)
 *   - /matches/:matchId (standalone full-width detail)
 *   - /matches/compare (each column uses the same data shape)
 *
 * Backend contract:
 *   GET /v1/admin/matches/:matchId
 *
 * Spec: docs/superpowers/specs/2026-05-28-admin-matches-summaries-design.md §7.2 / §6.2
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApiUrl } from '@refinedev/core';
import { adminFetch, AdminApiError } from '@/providers/admin-api';
import { useAdminPermissions } from '@/contexts/AdminPermissionsContext';
import { SummariesCard } from './SummariesCard';
import { FlagPanel } from './FlagPanel';
import { PayLinksCard } from './PayLinksCard';
import { AdminCreatedBadge } from './AdminCreatedBadge';
import { AuditCard, type AuditEntry } from './AuditCard';
import { UserProfileLink } from './UserProfileLink';
import { MatchJourneyCard, type MatchJourney } from './MatchJourneyCard';

interface UserRef {
  id: string;
  email: string | null;
  display_name: string | null;
  is_fake: boolean;
  is_tester: boolean;
  primary_photo_url?: string | null;
}

interface SafetyReviewRow {
  id: string;
  verdict: string;
  fail_codes: string[];
  rationale: string | null;
  evidence: unknown;
  model: string | null;
  latency_ms: number | null;
  admin_decision: string | null;
  created_at: string;
}

interface MatchDetailShape {
  match_id: string;
  matched_at: string;
  status: string;
  user1: UserRef;
  user2: UserRef;
  compatibility_score: number | null;
  origin_kind: string;
  has_tester_bypass_artifact: boolean;
  admin_flagged_at: string | null;
  admin_flag_reason: string | null;
  admin_flagged_by_admin_id?: string | null;
  flag_source?: 'admin' | 'safety_rail' | null;
  safety_review_status?: string;
  safety_review_acked_at?: string | null;
  created_by_admin_id: string | null;
  admin_create_reason?: string | null;
  admin_create_force_dealbreakers?: boolean;
  recent_admin_actions?: AuditEntry[];
  safety_reviews?: SafetyReviewRow[];
  funnel?: { presented_at?: string | null };
  journey?: MatchJourney;
}

interface Props {
  matchId: string;
}

export function MatchDetail({ matchId }: Props) {
  const apiUrl = useApiUrl();
  const { hasPermission } = useAdminPermissions();
  const [match, setMatch] = useState<MatchDetailShape | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await adminFetch(`${apiUrl}/v1/admin/matches/${matchId}`);
      const json = (await r.json()) as MatchDetailShape;
      setMatch(json);
    } catch (e: unknown) {
      setErr(e instanceof AdminApiError ? e.message : 'Failed to load match');
    } finally {
      setLoading(false);
    }
  }, [apiUrl, matchId]);

  useEffect(() => {
    reload();
  }, [reload]);

  if (loading && !match) {
    return (
      <div className="p-4 text-sm text-muted-foreground">Loading…</div>
    );
  }

  if (err && !match) {
    return (
      <div className="m-4 rounded-md bg-destructive/10 p-4 text-sm text-destructive">
        {err}
      </div>
    );
  }

  if (!match) return null;

  return (
    <div style={{ padding: 16 }}>
      <Header match={match} />

      {match.created_by_admin_id && <AdminCreatedBadge match={match} />}

      {match.has_tester_bypass_artifact && (
        <div className="mb-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-2 text-[13px] text-amber-600 dark:text-amber-400">
          ⚠ Tester-bypass artifact detected (TRUE share/view flags with
          NULL timestamps — should be impossible post-PR #1089). Investigate.
        </div>
      )}

      <MatchJourneyCard
        journey={match.journey}
        nameA={participantLabel(match.user1)}
        nameB={participantLabel(match.user2)}
      />

      <FlagPanel match={match} onChange={reload} />

      <SafetyReviewCard match={match} onChange={reload} />

      {/* PR 3 (wants→has scoring rollout, spec §8.1):
          Deep link to the scoring-diagnostic view. Same permission gate
          as the current page (matches.read) — the diagnostic surfaces
          the same match data, just with the shadow scoring breakdown. */}
      <div className="mb-2 flex justify-end">
        <Link
          to={`/matches/${matchId}/scoring-diagnostic`}
          className="text-xs text-primary no-underline hover:underline"
        >
          View scoring diagnostic →
        </Link>
      </div>

      {hasPermission('matches.summaries.read') && (
        <SummariesCard matchId={matchId} match={match} />
      )}

      {/* Abby Pay pay-link minting (gated matches.create inside the card;
          backend: POST /v1/admin/matches/:matchId/pay-links).
          key={match.match_id} forces a remount when the displayed match
          changes, wiping minted links / busy / error state — otherwise
          match A's signed payment URLs would keep rendering under match B
          (wrong-recipient token misdelivery on a money path). */}
      <PayLinksCard key={match.match_id} match={match} />

      <AuditCard entries={match.recent_admin_actions ?? []} />
    </div>
  );
}

function Header({ match }: { match: MatchDetailShape }) {
  return (
    <div className="mb-2 rounded-md border bg-card p-3 text-card-foreground">
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto 1fr',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <UserHeader user={match.user1} />
        <div style={{ textAlign: 'center' }}>
          <div
            className={`text-2xl font-bold ${scoreColorClass(match.compatibility_score)}`}
          >
            {match.compatibility_score ?? '—'}
          </div>
          <div className="text-[11px] text-muted-foreground">compatibility</div>
        </div>
        <UserHeader user={match.user2} align="right" />
      </div>

      <div
        style={{
          marginTop: 10,
          display: 'flex',
          gap: 6,
          flexWrap: 'wrap',
          fontSize: 12,
        }}
      >
        <Pill label={`Status: ${match.status}`} />
        <Pill label={`Origin: ${originLabel(match.origin_kind)}`} />
        <Pill
          label={`Matched: ${match.matched_at ? new Date(match.matched_at).toLocaleString() : '—'}`}
        />
        {match.admin_flagged_at && (
          <Pill
            label={match.flag_source === 'safety_rail' ? '⚑ Safety rail' : '⚑ Flagged'}
            tone="warn"
          />
        )}
        {match.safety_review_status && match.safety_review_status !== 'not_required' && (
          <Pill
            label={`Review: ${match.safety_review_status}`}
            tone={match.safety_review_status === 'fail' || match.safety_review_status === 'error' ? 'warn' : 'gray'}
          />
        )}
      </div>
    </div>
  );
}

/** The name an admin sees for a participant: display name, else email, else id prefix. */
function participantLabel(user: UserRef): string {
  return user.display_name || user.email || user.id.slice(0, 8);
}

function UserHeader({
  user,
  align,
}: {
  user: UserRef;
  align?: 'left' | 'right';
}) {
  const label = participantLabel(user);
  const photo = renderablePhotoUrl(user.primary_photo_url);
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: align === 'right' ? 'flex-end' : 'flex-start',
        gap: 4,
      }}
    >
      {photo ? (
        <img
          src={photo}
          alt={`${label} photo`}
          className="border"
          style={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            objectFit: 'cover',
          }}
          onError={e => {
            (e.currentTarget as HTMLImageElement).style.display = 'none';
          }}
        />
      ) : (
        <div
          className="bg-muted font-semibold text-muted-foreground"
          style={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {label[0]?.toUpperCase() ?? '?'}
        </div>
      )}
      <div style={{ fontWeight: 600, fontSize: 14 }}>
        <UserProfileLink userId={user.id}>{label}</UserProfileLink>
      </div>
      {user.email && user.display_name && (
        <div className="text-[11px] text-muted-foreground">{user.email}</div>
      )}
      <div style={{ display: 'flex', gap: 4 }}>
        {user.is_fake && <Pill label="fake" tone="violet" small />}
        {user.is_tester && !user.is_fake && <Pill label="tester" tone="sky" small />}
      </div>
    </div>
  );
}

function SafetyReviewCard({
  match,
  onChange,
}: {
  match: MatchDetailShape;
  onChange?: () => void;
}) {
  const apiUrl = useApiUrl();
  const { hasPermission } = useAdminPermissions();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const reviews = match.safety_reviews ?? [];
  const status = match.safety_review_status;
  if (!status || status === 'not_required') return null;

  const canForce =
    hasPermission('matches.flag') &&
    (status === 'fail' || status === 'error') &&
    !match.funnel?.presented_at;

  const forcePresent = async () => {
    if (!window.confirm('Mark this vetoed match as approved? It will present to both users on the next 5-minute cycle. Use only for a confirmed false positive.')) {
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await adminFetch(
        `${apiUrl}/v1/admin/matches/${match.match_id}/safety-review/force-present`,
        { method: 'POST' },
      );
      onChange?.();
    } catch (e: unknown) {
      setErr(e instanceof AdminApiError ? e.message : 'Force-present failed');
    } finally {
      setBusy(false);
    }
  };

  const latest = reviews[0];
  return (
    <div className="mt-2 rounded-md border border-amber-500/40 bg-card px-2.5 py-2 text-[13px]">
      <div className="mb-1 font-semibold">Safety review</div>
      <div>Status: <strong>{status}</strong>{latest ? ` · ${latest.verdict}` : ''}</div>
      {latest?.fail_codes?.length ? (
        <div>Codes: {latest.fail_codes.join(', ')}</div>
      ) : null}
      {latest?.rationale ? (
        <div className="mt-1 text-muted-foreground">{latest.rationale}</div>
      ) : null}
      {canForce && (
        <button
          type="button"
          onClick={forcePresent}
          disabled={busy}
          className="mt-2 cursor-pointer rounded border bg-background px-2.5 py-1.5 text-[13px] hover:bg-muted"
        >
          {busy ? '…' : 'Force present'}
        </button>
      )}
      {err && <div className="mt-1.5 text-xs text-destructive">{err}</div>}
    </div>
  );
}

function Pill({
  label,
  tone = 'gray',
  small = false,
}: {
  label: string;
  tone?: 'gray' | 'warn' | 'violet' | 'sky';
  small?: boolean;
}) {
  const toneClass: Record<string, string> = {
    gray: 'bg-muted text-foreground',
    warn: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
    violet: 'bg-violet-600 text-white',
    sky: 'bg-sky-500 text-white',
  };
  return (
    <span
      className={`inline-block rounded-full ${
        small ? 'px-1.5 py-px text-[10px] uppercase tracking-wide' : 'px-2 py-0.5 text-xs'
      } ${toneClass[tone]}`}
    >
      {label}
    </span>
  );
}

/**
 * Photo URLs from the backend may be of the form `s3://bucket/key` (an S3
 * identifier, not a renderable HTTP URL). Don't try to render those — the
 * <img> will 404. CloudFront/signed-URL plumbing is a follow-up; for now we
 * fall back to the initial-letter avatar.
 */
function renderablePhotoUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  if (url.startsWith('s3://')) return null;
  return url;
}

function scoreColorClass(s: number | null): string {
  if (s === null || s === undefined) return 'text-muted-foreground';
  if (s >= 85) return 'text-green-500';
  if (s >= 65) return 'text-amber-500';
  return 'text-red-500';
}

function originLabel(kind: string) {
  switch (kind) {
    case 'organic':
      return 'Organic';
    case 'tester_instant':
      return 'Tester instant';
    case 'fake_paired':
      return 'Fake-paired';
    default:
      return kind;
  }
}
